import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const SuppliersDataGrid = ({ searchQuery, onEdit, onDelete, selectedItems, onSelectionChange }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const suppliersData = [
    {
      id: 'SUP001',
      name: 'Whitehaven Coal Limited',
      code: 'WHC',
      country: 'Australia',
      contactPerson: 'James Mitchell',
      email: 'j.mitchell@whitehaven.com.au',
      phone: '+61 2 8222 1100',
      status: 'active',
      contractTerms: 'FOB',
      paymentTerms: 'LC 90 days',
      performanceScore: 95,
      totalShipments: 142,
      lastUpdated: '2025-12-10',
      syncStatus: 'synced'
    },
    {
      id: 'SUP002',
      name: 'Glencore Coal Assets Australia',
      code: 'GCA',
      country: 'Australia',
      contactPerson: 'Sarah Thompson',
      email: 's.thompson@glencore.com.au',
      phone: '+61 7 3833 8000',
      status: 'active',
      contractTerms: 'CFR',
      paymentTerms: 'LC 60 days',
      performanceScore: 92,
      totalShipments: 198,
      lastUpdated: '2025-12-11',
      syncStatus: 'synced'
    },
    {
      id: 'SUP003',
      name: 'Yancoal Australia Limited',
      code: 'YAL',
      country: 'Australia',
      contactPerson: 'David Chen',
      email: 'd.chen@yancoal.com.au',
      phone: '+61 2 8583 5300',
      status: 'active',
      contractTerms: 'FOB',
      paymentTerms: 'LC 75 days',
      performanceScore: 88,
      totalShipments: 167,
      lastUpdated: '2025-12-09',
      syncStatus: 'pending'
    },
    {
      id: 'SUP004',
      name: 'Peabody Energy Australia',
      code: 'PEA',
      country: 'Australia',
      contactPerson: 'Michael Roberts',
      email: 'm.roberts@peabody.com.au',
      phone: '+61 7 3011 3000',
      status: 'active',
      contractTerms: 'FOB',
      paymentTerms: 'LC 90 days',
      performanceScore: 90,
      totalShipments: 134,
      lastUpdated: '2025-12-12',
      syncStatus: 'synced'
    },
    {
      id: 'SUP005',
      name: 'Coronado Global Resources',
      code: 'CGR',
      country: 'Australia',
      contactPerson: 'Emma Wilson',
      email: 'e.wilson@coronadoglobal.com',
      phone: '+61 7 3031 7777',
      status: 'inactive',
      contractTerms: 'CFR',
      paymentTerms: 'LC 60 days',
      performanceScore: 85,
      totalShipments: 89,
      lastUpdated: '2025-11-28',
      syncStatus: 'error'
    }
  ];

  const filteredData = suppliersData?.filter(supplier =>
    supplier?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    supplier?.code?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    supplier?.contactPerson?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = [...filteredData]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(sortedData?.map(item => item?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, id]);
    } else {
      onSelectionChange(selectedItems?.filter(itemId => itemId !== id));
    }
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'synced': return 'text-success';
      case 'pending': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left">
              <Checkbox
                checked={selectedItems?.length === sortedData?.length && sortedData?.length > 0}
                onChange={(e) => handleSelectAll(e?.target?.checked)}
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('code')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Code
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Supplier Name
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact Person</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact Details</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Terms</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('performanceScore')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Performance
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sync</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedData?.map((supplier) => (
            <tr key={supplier?.id} className="hover:bg-muted/30 transition-colors">
              <td className="px-4 py-3">
                <Checkbox
                  checked={selectedItems?.includes(supplier?.id)}
                  onChange={(e) => handleSelectItem(supplier?.id, e?.target?.checked)}
                />
              </td>
              <td className="px-4 py-3">
                <span className="font-mono text-sm font-medium">{supplier?.code}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">{supplier?.name}</span>
                  <span className="text-xs text-muted-foreground">{supplier?.country}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm">{supplier?.contactPerson}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Mail" size={12} />
                    <span>{supplier?.email}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Phone" size={12} />
                    <span>{supplier?.phone}</span>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs"><span className="font-medium">Contract:</span> {supplier?.contractTerms}</span>
                  <span className="text-xs"><span className="font-medium">Payment:</span> {supplier?.paymentTerms}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${supplier?.performanceScore}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{supplier?.performanceScore}%</span>
                </div>
                <span className="text-xs text-muted-foreground">{supplier?.totalShipments} shipments</span>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  supplier?.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    supplier?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                  }`} />
                  {supplier?.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Icon
                    name={supplier?.syncStatus === 'synced' ? 'CheckCircle2' : supplier?.syncStatus === 'pending' ? 'Clock' : 'AlertCircle'}
                    size={16}
                    className={getSyncStatusColor(supplier?.syncStatus)}
                  />
                  <span className={`text-xs ${getSyncStatusColor(supplier?.syncStatus)}`}>
                    {supplier?.syncStatus}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEdit(supplier)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onDelete(supplier?.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No suppliers found matching your search</p>
        </div>
      )}
    </div>
  );
};

export default SuppliersDataGrid;